import type { TutorialContent } from './types'

export const tutorialContent: TutorialContent = {
  "id": "badreor-ptw-help-learning",
  "version": "0.3-draft-2026-11-04",
  "title": "PTW training",
  "subtitle": "Work through a practice permit, check your responsibilities or look up a question.",
  "status": "Draft for HSE review",
  "roles": [
    {
      "code": "ALL",
      "label": "The whole journey",
      "taskLabel": "Show me the whole journey",
      "summary": "Follow the same job through all four stages.",
      "receives": "The proposed work and its supporting information.",
      "checks": "Who acts, which conditions are required and what happens next.",
      "does": "Follow one fictional job from preparation to closeout.",
      "handsTo": "The next responsible person shown at each step.",
      "stopIf": "The required authority or worksite controls are missing.",
      "stepIds": [
        "prepare",
        "review",
        "om-approve",
        "daily",
        "gas",
        "brief",
        "stop",
        "restart",
        "cancel",
        "closeout"
      ]
    },
    {
      "code": "PA",
      "label": "Permit Applicant",
      "taskLabel": "I prepare the work",
      "summary": "Give reviewers a clear, complete description of the actual job.",
      "receives": "The work request, actual site/equipment information and applicable method requirements.",
      "checks": "Scope, location, hazards, planning route and required supporting evidence match the same job.",
      "does": "Prepare and present the application, resolve review comments and brief the licensed PH after authorisation.",
      "handsTo": "The review team and RS, then the individual PH through the approved handover.",
      "stopIf": "Required information, controls or approval basis is missing; do not invent it.",
      "stepIds": [
        "prepare",
        "review",
        "daily",
        "cancel"
      ]
    },
    {
      "code": "RS",
      "label": "Responsible Supervisor",
      "taskLabel": "Authorise, pre-issue and validate the work",
      "summary": "Review and authorise the package; validate current conditions, confirm stops and close out.",
      "receives": "The work package, affected-area agreements and current planning, worksite and control evidence.",
      "checks": "Scope, classification, risk controls, independent OM approval, the sixteen daily checks, required examinations and person-level separation.",
      "does": "Authorise after review; pre-issue and validate with separate signatures; confirm stops and close out. The same RS or a second RS may validate. Gas-tester qualification is not required for the RS duty itself.",
      "handsTo": "OM for independent approval after authorisation; PH for their own acceptance after daily RS validation.",
      "stopIf": "Required controls, work scope, approvals or conflict resolution remain uncertain.",
      "stepIds": [
        "review",
        "daily",
        "gas",
        "stop",
        "restart",
        "closeout"
      ]
    },
    {
      "code": "OM",
      "label": "Operations Manager",
      "taskLabel": "Approve the authorised permit before any work",
      "summary": "Give the independent second check once per authorisation.",
      "receives": "The RS-authorised package, including scope, location, hazards, controls, SIMOPS and named people.",
      "checks": "That the package is fit to proceed and that you are a different person from its RS, PA and PH.",
      "does": "Record your approval after RS authorisation and before any daily cycle. A new authorisation needs a new OM approval.",
      "handsTo": "PA, PH or RS to request the first daily cycle.",
      "stopIf": "The package or a required control is unclear or unsafe; withhold approval.",
      "stepIds": [
        "om-approve"
      ]
    },
    {
      "code": "PH",
      "label": "Permit Holder",
      "taskLabel": "I lead and supervise the work",
      "summary": "Understand the job, check the safe site and stay in effective supervision.",
      "receives": "The authorised package, PA briefing and applicable current validation and control evidence.",
      "checks": "The actual worksite, required completed tests, controls, crew understanding and applicable permit conditions.",
      "does": "Brief and accept personally, supervise and pause/check, stop when required and cancel truthfully.",
      "handsTo": "The crew through the briefing, and RS through the required stop, handover or cancellation process.",
      "stopIf": "Work exceeds scope, conditions become unsafe or required controls or PH supervision are lost.",
      "stepIds": [
        "brief",
        "stop",
        "restart",
        "cancel"
      ]
    },
    {
      "code": "AGT",
      "label": "Authorised Gas Tester",
      "taskLabel": "I provide formal gas-test evidence",
      "summary": "Test within your actual qualification and record the factual result.",
      "receives": "The required test plan, actual location, approved method/criteria and current scope of authority.",
      "checks": "Your qualification, suitable instrument/method and applicable testing and recording requirements.",
      "does": "Perform and sign required formal tests, preserve unsafe results and tell PH to stop when required.",
      "handsTo": "PH and RS immediately, with the required escalation for an unsafe atmosphere.",
      "stopIf": "Required authority, method or criteria are missing, or testing reveals an unsafe condition.",
      "stepIds": [
        "gas",
        "stop"
      ]
    },
    {
      "code": "CREW",
      "label": "Work-party member",
      "taskLabel": "I work under the permit",
      "summary": "Understand your task and controls, ask questions and report changes.",
      "receives": "The actual worksite briefing, assigned task and emergency/Pause & Check arrangements.",
      "checks": "You understand the boundary, hazards, controls and what to do if conditions change.",
      "does": "Give your own required acknowledgement, follow the agreed work and raise hazards promptly.",
      "handsTo": "PH for questions and changing conditions, using the approved immediate alarm route when needed.",
      "stopIf": "You do not understand the work or a hazard or control failure develops.",
      "stepIds": [
        "brief",
        "stop"
      ]
    },
    {
      "code": "OAC",
      "label": "Other Affected Custodian",
      "taskLabel": "I check risks to my affected area",
      "summary": "Bring the affected area's hazards and precautions into the job review.",
      "receives": "The actual work package and information about interactions with your area.",
      "checks": "Your affected-area licensed RS authority and the hazards, precautions and package revision.",
      "does": "Jointly review the work, add necessary precautions and give your own agreement only when satisfied.",
      "handsTo": "The review team and RS, with clear agreed interface controls.",
      "stopIf": "The interaction, required precautions or your authority for that area is unresolved.",
      "stepIds": [
        "review",
        "stop"
      ]
    }
  ],
  "lessons": [
    {
      "id": "cur-01",
      "title": "PTW essentials",
      "summary": "Understand what the permit controls before following the screens.",
      "roles": [
        "ALL"
      ],
      "objectives": [
        "Point to the specific work, equipment/location, people, current revision and authorised interval.",
        "Explain how the PTW, JHSEP, TRIC and relevant supporting documents work together.",
        "Recognise a synthetic exercise and identify the governing procedure/profile before real work."
      ],
      "sections": [
        {
          "heading": "Start with the actual job",
          "body": "A permit coordinates a defined job, place, people, period and controls. It links the job risk assessment, briefing and any supporting certificates. A permit number or a green screen cannot prove that the physical worksite is safe.",
          "bullets": [
            "Read the scope, location, current revision and work boundary.",
            "Use the governing company/client procedure and actual task assessment.",
            "Stop if the work or conditions no longer match the agreed package."
          ]
        },
        {
          "heading": "Activity labels describe the work",
          "body": "The programme covers Well Testing, OTSG, Water Treatment, Chemical Injection, Hot Water Pumping, Workshop and Surface Preparation. These service names do not select the permit class. Describe the real method, equipment, location and hazards.",
          "bullets": [
            "For Surface Preparation, record the actual preparation method.",
            "Keep Hydrostatic, Pneumatic and Hydraulic pressure-test labels distinct, with actual medium, method and approved procedure.",
            "A previous permit or workshop location does not automatically establish today’s route."
          ]
        }
      ],
      "remember": [
        "Avoid: treating a permit number, green status or uploaded form as proof that a site is safe.",
        "Avoid: assuming a previous job, service group or workshop location determines today's class.",
        "Avoid: treating a synthetic policy or draft procedure as approved operating authority."
      ],
      "checks": [
        {
          "id": "cur-01-check-1",
          "prompt": "Similar work was permitted last month. What do you do today?",
          "options": [
            {
              "id": "other-1",
              "text": "Copy last month's signed package.",
              "feedback": "Review the actual current job, hazards, controls and permit route before relying on copied material."
            },
            {
              "id": "correct",
              "text": "Review today's task and conditions.",
              "feedback": "Previous work can help preparation, but today's scope, location, hazards, controls and associated documents still need review."
            },
            {
              "id": "other-2",
              "text": "Use the old location approval.",
              "feedback": "An old approval does not establish the current task, conditions or work authority."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-01-check-2",
          "prompt": "The job is labelled Surface Preparation. What selects its permit route?",
          "options": [
            {
              "id": "correct",
              "text": "Assess actual method, location and hazards.",
              "feedback": "Use the applicable classification process for the real task; the service name alone does not select a class, and pressure-test labels retain their actual medium/method distinctions."
            },
            {
              "id": "other-1",
              "text": "Use the service name on its own.",
              "feedback": "The service label does not describe enough of the actual job to determine its route."
            },
            {
              "id": "other-2",
              "text": "Use the permit class from last time.",
              "feedback": "The current method, location and hazards may require a different route."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-002",
        "CORE-007",
        "CORE-017",
        "CORE-022",
        "CORE-032",
        "CORE-033",
        "ACT-066",
        "ACT-069",
        "ACT-070",
        "SYS-001",
        "SYS-002"
      ]
    },
    {
      "id": "cur-02",
      "title": "Your identity and authority",
      "summary": "Separate your learning path, account access and authority to sign.",
      "roles": [
        "ALL"
      ],
      "objectives": [
        "Distinguish account access, tutorial progress, training, competence assessment, licensing and appointment.",
        "Check the actual person's area/work scope and attempted action.",
        "Recognise prohibited same-permit role combinations and avoid self-authorisation."
      ],
      "sections": [
        {
          "heading": "A learning path is not an appointment",
          "body": "Choosing a role here changes the lessons shown. It does not change your platform account or authorise you to sign. Training, competence assessment, licensing and appointment follow the applicable approved scheme.",
          "bullets": [
            "Use your own approved account for real sign-offs.",
            "Administrator access does not confer operational authority.",
            "A tutorial badge or a practice answer is not a licence or permit."
          ]
        },
        {
          "heading": "Who does what",
          "body": "The PA prepares the work; the RS reviews/authorises and pre-issues; the RS validates the work cycle; the PH briefs, accepts and supervises; the AGT performs authorised gas testing. OAC, PC and isolation functions have their own applicable authority and scope.",
          "bullets": [
            "Do not sign for another person or authorise your own work.",
            "Being able to act in two roles does not mean you may combine them on the same permit.",
            "Keep earlier valid sign-offs as evidence while checking current authority for new actions and ongoing assignments."
          ]
        },
        {
          "heading": "The BadrEOR RS and independent OM",
          "body": "The RS reviews, authorises, pre-issues, validates each cycle, confirms stops and closes out. The OM independently approves each authorisation before any cycle. Formal gas tests remain with a qualified AGT.",
          "bullets": [
            "An RS licence may support acting as RS, PA or PH; a PA as PA or PH; a PH as PH only. An OM appointment is for OM.",
            "On one permit, never combine RS with PA or PH, or OM with RS, PA or PH. The PA/PH exception route is not built and remains blocked.",
            "Pre-issue and validation are separate signatures. The same RS or a second RS may perform validation."
          ]
        }
      ],
      "remember": [
        "Avoid: believing a course-completion badge or software administrator role grants PTW signing authority.",
        "Avoid: using another person's account or treating crew membership as the PH's identity.",
        "Avoid: assuming a higher licence permits every combination on the same permit."
      ],
      "checks": [
        {
          "id": "cur-02-check-1",
          "prompt": "Someone finishes this tutorial. What happens to operational signing authority?",
          "options": [
            {
              "id": "other-1",
              "text": "Grant authority when tutorial is complete.",
              "feedback": "Tutorial completion does not grant an operating licence, competence finding or appointment."
            },
            {
              "id": "other-2",
              "text": "Treat completion as full competence assessment.",
              "feedback": "Learning progress does not replace the applicable approved assessment and licensing route."
            },
            {
              "id": "correct",
              "text": "Record learning; follow approved qualification route.",
              "feedback": "Training, competence assessment, licensing and appointment remain separate requirements under the applicable approved scheme; this tutorial creates no licence or pass threshold."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-02-check-2",
          "prompt": "An RS will perform the permit work. Who must authorise it?",
          "options": [
            {
              "id": "correct",
              "text": "Another appropriately authorised RS.",
              "feedback": "No one may authorise work they will perform; being qualified for several roles does not waive the applicable person-level separation requirements."
            },
            {
              "id": "other-1",
              "text": "The same RS using another role.",
              "feedback": "Changing the selected role does not change the person or remove the self-authorisation prohibition."
            },
            {
              "id": "other-2",
              "text": "The PH acting as approver.",
              "feedback": "PH acceptance does not supply the required RS authorisation or waive separation."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-004",
        "CORE-005",
        "CORE-049",
        "CORE-074",
        "CORE-075",
        "GOV-004",
        "GOV-046",
        "GOV-048",
        "GOV-049",
        "GOV-050",
        "GOV-051",
        "GOV-052",
        "GOV-060",
        "GOV-061",
        "GOV-064",
        "SYS-004"
      ]
    },
    {
      "id": "cur-03",
      "title": "Prepare the application",
      "summary": "Describe the job so reviewers can assess the real scope and controls.",
      "roles": [
        "PA",
        "RS"
      ],
      "objectives": [
        "Describe one clear task boundary, exact equipment/location and proposed interval.",
        "Prepare JHSEP, relevant controls and supporting evidence using actual source information.",
        "Submit for review and brief the individual PH after authorisation."
      ],
      "sections": [
        {
          "heading": "Build a clear package",
          "body": "The Permit Applicant records the work, equipment/location, boundary, requested period, people, hazards, controls and supporting documents. The risk assessment and method statement must describe the same work as the application.",
          "bullets": [
            "Include the actual pressure-test medium/method or preparation method where relevant.",
            "Identify nearby work, affected areas and required certificates.",
            "Use approved criteria; obtain missing information instead of guessing."
          ]
        },
        {
          "heading": "Submit the correct revision",
          "body": "Before submission, check that the package is complete and understandable. Reviewers and signatories must act on the actual revision. A significant scope change needs the appropriate revision and renewed review, rather than a signature on an outdated package.",
          "bullets": [
            "Urgency needs the approved justification/review route and does not remove safety checks.",
            "Do not quietly change signed evidence.",
            "An attachment records evidence; its presence alone does not verify its conditions."
          ]
        }
      ],
      "remember": [
        "Avoid: submitting vague maintenance wording or copying old hazards and locations.",
        "Avoid: guessing H2S zones, gas limits, risk scores or isolation design.",
        "Avoid: adding a materially changed scope after signatures without a new review of the affected revision."
      ],
      "checks": [
        {
          "id": "cur-03-check-1",
          "prompt": "A required area classification or acceptance criterion is missing. What next?",
          "options": [
            {
              "id": "other-2",
              "text": "Leave blank and assume it passes.",
              "feedback": "A missing required criterion remains unresolved and cannot silently pass."
            },
            {
              "id": "correct",
              "text": "Obtain the applicable approved basis.",
              "feedback": "Use approved area information and technical criteria; keep the affected route blocked until its required evidence is available."
            },
            {
              "id": "other-1",
              "text": "Use the most conservative remembered value.",
              "feedback": "A remembered value, even described as conservative, does not establish the applicable approved criterion."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-03-check-2",
          "prompt": "The meeting identifies a significant scope change. What should happen?",
          "options": [
            {
              "id": "other-1",
              "text": "Sign, then amend the package.",
              "feedback": "Signatures must refer to the actual package assessed, not an outdated scope."
            },
            {
              "id": "other-2",
              "text": "Add it only to briefing.",
              "feedback": "A toolbox talk cannot authorise additional work outside the permit boundary."
            },
            {
              "id": "correct",
              "text": "Revise and resubmit for review.",
              "feedback": "Significant changes require resubmission through the required review; minor pre-authorisation changes have their own understood/agreed route."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-006",
        "CORE-016",
        "CORE-017",
        "CORE-026",
        "CORE-027",
        "CORE-028",
        "CORE-029",
        "CORE-031",
        "CORE-032",
        "CORE-033",
        "CORE-034",
        "CORE-045",
        "CORE-046",
        "CORE-051"
      ]
    },
    {
      "id": "cur-04",
      "title": "Review, authorise and pre-issue",
      "summary": "Keep overall authorisation separate from each work cycle.",
      "roles": [
        "RS",
        "OAC"
      ],
      "objectives": [
        "Review the work, hazards, controls, permit route, conflicts and supporting approvals.",
        "Authorise only after the meeting requirements are satisfied and preserve own accountability.",
        "Pre-issue every daily validation cycle against the current plan, conflicts and affected custodians."
      ],
      "sections": [
        {
          "heading": "Review the proposed job",
          "body": "The RS reviews the scope, classification, risk controls, supporting documents, affected areas and planning/conflict position through the approved process. Authorisation refers to a particular work package and overall interval; it is not permission to start every day without further checks.",
          "bullets": [
            "Check the actual role authority and separation requirements.",
            "Resolve missing approvals or technical interpretations before the affected route proceeds.",
            "Return significant changes for the required revision and review."
          ]
        },
        {
          "heading": "Repeat the daily pre-issue review",
          "body": "For the ordinary Class B cycle, RS pre-issue checks the current plan or approved change, conflicts, SIMOPS controls and affected custodians. A remaining overall authorisation period does not replace this current review.",
          "bullets": [
            "Review the real location and surrounding work.",
            "Use current constraints rather than yesterday’s assumptions.",
            "Do not bypass an unresolved approval because the exercise is labelled Class B."
          ]
        }
      ],
      "remember": [
        "Avoid: treating authorisation as permission to begin before validation and PH acceptance.",
        "Avoid: assuming a prior day's pre-issue covers today's changed conditions.",
        "Avoid: silently treating every Class B case as needing or never needing PC approval."
      ],
      "checks": [
        {
          "id": "cur-04-check-1",
          "prompt": "The permit is still authorised. What is needed for today's daily validation?",
          "options": [
            {
              "id": "correct",
              "text": "RS reviews today's cycle again.",
              "feedback": "Each daily validation needs the current RS pre-issue review of the plan, conflicts and applicable interface controls."
            },
            {
              "id": "other-1",
              "text": "Use yesterday's pre-issue without review.",
              "feedback": "Yesterday's review does not replace today's current checks."
            },
            {
              "id": "other-2",
              "text": "Skip RS; ask PH to accept.",
              "feedback": "PH acceptance cannot replace RS pre-issue or RS validation."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-04-check-2",
          "prompt": "A required isolation-related approval is unclear. What should RS do?",
          "options": [
            {
              "id": "other-1",
              "text": "Proceed under yesterday's permit approval.",
              "feedback": "An earlier case does not resolve the approval basis for today's affected work."
            },
            {
              "id": "correct",
              "text": "Resolve it before work proceeds.",
              "feedback": "Obtain the authorised interpretation and required approval evidence; a synthetic Class B example cannot resolve the source conflict or grant PC approval."
            },
            {
              "id": "other-2",
              "text": "Treat Class B as exemption.",
              "feedback": "The Class B label does not waive an applicable approval or unresolved isolation condition."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-009",
        "CORE-025",
        "CORE-035",
        "CORE-045",
        "CORE-046",
        "CORE-047",
        "CORE-048",
        "CORE-049",
        "CORE-052",
        "CORE-066"
      ]
    },
    {
      "id": "cur-om",
      "title": "Operations Manager approval before work",
      "summary": "Recognise the independent approval between RS authorisation and the first daily cycle.",
      "roles": [
        "OM",
        "RS",
        "PA",
        "PH"
      ],
      "objectives": [
        "Explain when OM approval is required.",
        "Keep OM independence separate from RS authorisation.",
        "Recognise when a new approval is needed."
      ],
      "sections": [
        {
          "heading": "An independent check of the authorised package",
          "body": "After the RS authorises, the OM checks the scope, location, hazards and controls, SIMOPS and the people named. The OM must be different from the RS, PA and PH of this permit. Approval is recorded once for that authorisation.",
          "bullets": [
            "No daily cycle may be requested before OM approval.",
            "OM approval does not replace RS authorisation, daily pre-issue, validation or PH acceptance."
          ]
        },
        {
          "heading": "Keep approval tied to the authorisation",
          "body": "A new authorisation needs a new OM approval. A renewed daily cycle after a stop under the same authorisation does not itself need another OM approval.",
          "bullets": [
            "Read OM_APPROVAL_REQUIRED as a missing approval, not a reason to change role or bypass the control.",
            "Unclear or unsafe conditions mean approval must be withheld."
          ]
        }
      ],
      "remember": [
        "RS authorisation is followed by independent OM approval before a cycle.",
        "A new authorisation needs a new OM approval."
      ],
      "checks": [
        {
          "id": "cur-om-check-1",
          "prompt": "The RS has authorised; OM approval is pending. Can the first cycle be requested?",
          "options": [
            {
              "id": "correct",
              "text": "No. Record the independent OM approval first.",
              "feedback": "The authorisation still needs the OM approval before any daily cycle."
            },
            {
              "id": "other-1",
              "text": "Yes, because the RS has signed.",
              "feedback": "RS authorisation does not replace the independent OM approval."
            },
            {
              "id": "other-2",
              "text": "Yes, if the PH accepts early.",
              "feedback": "PH acceptance cannot supply the missing OM approval."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": []
    },
    {
      "id": "cur-05",
      "title": "Affected areas and additional approvals",
      "summary": "Understand OAC agreement and conditional PC involvement.",
      "roles": [
        "PA",
        "RS",
        "OAC"
      ],
      "objectives": [
        "Recognise when another area affects or is affected by the job.",
        "Add affected-area hazards and precautions to the same JHSEP and countersign only when satisfied.",
        "Distinguish OAC agreement, PC approval and RS authorisation."
      ],
      "sections": [
        {
          "heading": "Agree the affected-area controls",
          "body": "The Other Affected Custodian represents the affected operational area. Under the reference role scheme, OAC needs the relevant affected-area licensed RS authority. Agreement includes the actual hazards, precautions and package revision; it is not an unexplained tick.",
          "bullets": [
            "Identify who controls the affected equipment/area.",
            "Record additional hazards and precautions in the linked package.",
            "Obtain the required agreement against the correct revision."
          ]
        },
        {
          "heading": "Use the required approval route",
          "body": "Production Coordinator involvement depends on the governing route and conditions. Do not assume PC approval is required for every ordinary Class B example, or omit it where it is required. If the requirement is unresolved, obtain the authorised interpretation.",
          "bullets": [
            "A nearby supervisor is not automatically an OAC.",
            "A role label in a screen does not establish its scope.",
            "This lesson does not resolve outstanding isolation or client-approval interpretations."
          ]
        }
      ],
      "remember": [
        "Avoid: assigning OAC to any supervisor instead of a licensed RS for the affected area.",
        "Avoid: having a meeting recorder create another person's sign-off.",
        "Avoid: reusing an old countersignature after the reviewed hazards or scope materially change."
      ],
      "checks": [
        {
          "id": "cur-05-check-1",
          "prompt": "Who can give the required OAC agreement under the reference scheme?",
          "options": [
            {
              "id": "other-1",
              "text": "Any supervisor in the vicinity.",
              "feedback": "Proximity does not establish the affected-area authority or actual hazard review."
            },
            {
              "id": "other-2",
              "text": "The person organising this meeting.",
              "feedback": "Meeting organisation does not grant OAC authority or allow a recorder to sign for the custodian."
            },
            {
              "id": "correct",
              "text": "The affected-area licensed RS.",
              "feedback": "OAC needs the relevant affected-area licensed RS authority and must actually assess the hazards and precautions."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-05-check-2",
          "prompt": "OAC identifies an additional hazard. What should the package show?",
          "options": [
            {
              "id": "correct",
              "text": "Add precautions before OAC agreement.",
              "feedback": "Include the hazard and precautions in the linked JHSEP/package; OAC agrees to the relevant revision only when satisfied."
            },
            {
              "id": "other-1",
              "text": "Record agreement; add precautions later.",
              "feedback": "The agreement must include the actual hazards and precautions being accepted."
            },
            {
              "id": "other-2",
              "text": "Keep it outside permit review.",
              "feedback": "The affected-area interface belongs in the coordinated review and required controls."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-004",
        "CORE-005",
        "CORE-045",
        "CORE-046",
        "CORE-047",
        "CORE-048",
        "CORE-049"
      ]
    },
    {
      "id": "cur-06",
      "title": "Validate today’s cycle",
      "summary": "Check current conditions before the Permit Holder accepts.",
      "roles": [
        "PA",
        "RS",
        "PH",
        "AGT"
      ],
      "objectives": [
        "Work through the applicable sixteen pre-validation checks using real evidence.",
        "Distinguish a completed gas test from a test prearranged before work.",
        "Refuse validation when doubt, inadequate resources or missing required controls remain."
      ],
      "sections": [
        {
          "heading": "Validation is a current assessment",
          "body": "The RS checks the daily cycle, worksite examination requirements and the validation subjects: plan, scope understanding, conflicts, dynamic risk, prerequisite controls, gas arrangements, isolations, checklists, vehicles, methods and relevant worksite evidence. Withhold validation when required controls or resources are doubtful.",
          "bullets": [
            "Use the actual governing checklist and current worksite information.",
            "Confirm the PH understands the work and restrictions.",
            "A checklist entry cannot waive a physical prerequisite."
          ]
        },
        {
          "heading": "Pre-arranged is not completed",
          "body": "The reference permits a required gas test to be pre-arranged at validation for completion before work starts. That entry does not allow work to start without the actual required acceptable test and other before-work conditions. PH acceptance is a separate attributable action.",
          "bullets": [
            "A prior acceptance does not authorise a new cycle.",
            "Do not confuse overall authorisation, RS validation, PH acceptance and permission to proceed.",
            "Read and satisfy the actual before-work blockers for your approved route; a missing requirement is not permission to proceed."
          ]
        },
        {
          "heading": "Separate daily RS signatures",
          "body": "Pre-issue and validation are separate records even when the same RS performs both. A second RS may validate instead. The sixteen checks and the first-day worksite examination remain part of validation.",
          "bullets": [
            "No cycle can be requested until the OM approves its authorisation.",
            "The RS does not need AGT qualification for validation; formal tests are signed by the AGT."
          ]
        }
      ],
      "remember": [
        "Avoid: treating validation as proof that the PH has accepted or that every before-work condition is already complete.",
        "Avoid: ticking a required isolation or worksite examination without actual verification.",
        "Avoid: treating a nominal validation duration as a guarantee of continuing eligibility."
      ],
      "checks": [
        {
          "id": "cur-06-check-1",
          "prompt": "RS has prearranged a required gas test. It has not happened. What next?",
          "options": [
            {
              "id": "other-2",
              "text": "Start with yesterday's test record.",
              "feedback": "A prior result does not supply the current required actual before-work test."
            },
            {
              "id": "correct",
              "text": "Complete testing before starting work.",
              "feedback": "Prearrangement at validation is permitted under the reference route, but work needs the actual required acceptable test, other controls and the required PH acceptance."
            },
            {
              "id": "other-1",
              "text": "Start after RS validation alone.",
              "feedback": "Validation with a prearranged test does not mean that test has been completed or work may begin."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-06-check-2",
          "prompt": "Required controls or RS resources are doubtful. What should RS do?",
          "options": [
            {
              "id": "other-1",
              "text": "Validate; ask PH to fix.",
              "feedback": "Validation cannot be used to waive missing prerequisites or inadequate resources."
            },
            {
              "id": "other-2",
              "text": "Extend validation to cover delays.",
              "feedback": "A longer period does not resolve inadequate controls or resources."
            },
            {
              "id": "correct",
              "text": "Withhold validation and tell supervisor.",
              "feedback": "RS withholds validation and escalates through the applicable process; a role licence does not override unsafe conditions or insufficient resources."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-035",
        "CORE-036",
        "CORE-038",
        "CORE-040",
        "CORE-052",
        "CORE-053",
        "CORE-055",
        "CORE-056",
        "CORE-058",
        "CORE-066"
      ]
    },
    {
      "id": "cur-07",
      "title": "Gas-test evidence and monitoring",
      "summary": "Recognise the evidence and response required; this is not a gas-testing qualification.",
      "roles": [
        "AGT",
        "RS",
        "PH"
      ],
      "objectives": [
        "Record the actual tester, test time, readings, units, instrument/method and applicable approved criterion reference.",
        "Keep formal signed tests distinct from continuous monitoring.",
        "Record unsafe facts, tell PH to stop and escalate rather than conceal or overwrite a result."
      ],
      "sections": [
        {
          "heading": "Record the factual test",
          "body": "Formal gas tests require the appropriate current tester authority, instrument/method and approved criteria. Record the actual species, units, readings, location and observation time with the required evidence. The later recording time does not make an older test fresh.",
          "bullets": [
            "Use the applicable approved criteria and timing plan.",
            "Continuous-monitor familiarity does not itself confer formal AGT authority.",
            "This tutorial supplies no gas limits, sampling method or calibration interval."
          ]
        },
        {
          "heading": "Keep unsafe evidence and stop",
          "body": "An unsafe or uncertain result is evidence to preserve, not a value to discard so work remains eligible. Stop the affected work and use the governing notifications/suspension route. Restart requires cause resolution and the required renewed controls; another acceptable reading alone is insufficient.",
          "bullets": [
            "Keep formal tests and continuous monitoring distinct.",
            "Do not reuse results for another location or method without an approved basis.",
            "Confined-space-specific timing and monitoring remain separate from generic worksite rules."
          ]
        }
      ],
      "remember": [
        "Avoid: entering a safe declaration without an approved applicable criterion.",
        "Avoid: replacing actual test time with a later data-entry time.",
        "Avoid: believing monitor training alone authorises formal AGT testing or that later acceptable readings erase an unsafe event."
      ],
      "checks": [
        {
          "id": "cur-07-check-1",
          "prompt": "PH knows how to use a monitor. What establishes authority to sign formal gas tests?",
          "options": [
            {
              "id": "correct",
              "text": "Check current formal AGT qualification.",
              "feedback": "Formal testing needs the relevant current AGT qualification and scope; monitor training alone is insufficient, In BadrEOR the RS needs no gas-tester certificate; formal tests are signed by a qualified AGT."
            },
            {
              "id": "other-1",
              "text": "Use PH monitor training alone.",
              "feedback": "Continuous-monitor use and formal gas testing have distinct competence and authority requirements."
            },
            {
              "id": "other-2",
              "text": "Reuse another tester's earlier signature.",
              "feedback": "A prior signature cannot authorise a new test or be supplied as another person's action."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-07-check-2",
          "prompt": "A formal test indicates an unsafe atmosphere. What is the safe response?",
          "options": [
            {
              "id": "other-1",
              "text": "Continue while repeating the gas test.",
              "feedback": "Do not continue unsafe work while waiting for a more acceptable reading."
            },
            {
              "id": "correct",
              "text": "Keep result; stop and escalate.",
              "feedback": "Preserve the factual result, stop work and follow the required suspension/notification route; resolve the cause and complete the required authorised restart process."
            },
            {
              "id": "other-2",
              "text": "Discard result and enter another.",
              "feedback": "Unsafe readings are safety evidence; another reading must not erase the unsafe event."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-036",
        "CORE-037",
        "CORE-038",
        "CORE-040",
        "CORE-041",
        "CORE-044",
        "CORE-055",
        "CORE-059",
        "CORE-061",
        "CORE-068"
      ]
    },
    {
      "id": "cur-08",
      "title": "Brief, accept and supervise",
      "summary": "Know the separate actions of the PH and each crew member.",
      "roles": [
        "PA",
        "RS",
        "PH",
        "CREW"
      ],
      "objectives": [
        "PH verifies site readiness, current validation, controls and required completed tests before own acceptance.",
        "Give the worksite toolbox talk and use TRIC for the actual work, hazards, responsibilities, DRA plan and questions.",
        "Maintain supervision, individual crew understanding, Pause & Check and proper handover."
      ],
      "sections": [
        {
          "heading": "Make the briefing meaningful",
          "body": "The PH explains the actual work, hazards, controls, responsibilities, conflicts, emergency arrangements and the dynamic review/Pause & Check plan. Each participant must understand and acknowledge their own attendance; another person cannot supply their electronic signature.",
          "bullets": [
            "Ask questions when a control or instruction is unclear.",
            "Crew understanding is distinct from PH acceptance.",
            "The PH must satisfy themselves about the safe worksite and complete the required acceptance before work starts."
          ]
        },
        {
          "heading": "Stay within the boundary",
          "body": "The PH supervises the work and keeps the required controls in place. Work stops when the PH is not present to supervise the activity. Stop and follow the planned arrangements if a required control is lost. A TRIC or dynamic review cannot authorise additional work outside the permit boundary.",
          "bullets": [
            "Report new hazards or changed conditions promptly.",
            "Do not write an extra task onto a briefing and assume it is authorised.",
            "Use the approved handover/new briefing route when responsibility changes."
          ]
        }
      ],
      "remember": [
        "Avoid: accepting because the screen is green without checking the worksite.",
        "Avoid: treating a roster or briefing recorder's entry as another person's acknowledgement or PH acceptance.",
        "Avoid: adding new work through TRIC or walking away without valid supervision/handover."
      ],
      "checks": [
        {
          "id": "cur-08-check-1",
          "prompt": "Crew members have acknowledged the briefing. What remains separate?",
          "options": [
            {
              "id": "other-1",
              "text": "Attendance automatically accepts the permit.",
              "feedback": "Attendance and individual understanding do not supply PH safe-site acceptance."
            },
            {
              "id": "other-2",
              "text": "One crew member accepts for all.",
              "feedback": "Each person's acknowledgement and the individual PH's own acceptance remain attributable to their actual actors."
            },
            {
              "id": "correct",
              "text": "PH makes their own acceptance.",
              "feedback": "PH must satisfy themselves the actual site is safe and all applicable current conditions are met, then make their own required acceptance."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-08-check-2",
          "prompt": "A new hazard or extra task appears. What should happen?",
          "options": [
            {
              "id": "correct",
              "text": "Stop; reassess scope and authority.",
              "feedback": "Inform PH and stop as required; TRIC cannot enlarge the permit boundary, and different work needs the appropriate new permit/review route."
            },
            {
              "id": "other-1",
              "text": "Add task to TRIC; continue.",
              "feedback": "A TRIC entry does not authorise work beyond the permit's boundary."
            },
            {
              "id": "other-2",
              "text": "Continue until current permit expires.",
              "feedback": "An unexpired period does not allow changed hazards or different work to bypass reassessment and controls."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-051",
        "CORE-057",
        "CORE-058",
        "CORE-059",
        "CORE-060",
        "CORE-061",
        "CORE-067",
        "GOV-014",
        "GOV-050",
        "SYS-004"
      ]
    },
    {
      "id": "cur-09",
      "title": "Stop and restart safely",
      "summary": "Separate immediate safety action from formal suspension and renewed acceptance.",
      "roles": [
        "ALL"
      ],
      "objectives": [
        "Recognise unsafe/change/expiry/required-stop conditions and take physical safety action promptly.",
        "Distinguish reporting a fact from a PH or RS signature and preserve the actual actor/time.",
        "Follow the applicable suspension/alarm branch and obtain renewed validation and PH acceptance before restart."
      ],
      "sections": [
        {
          "heading": "Anyone can raise a dangerous condition",
          "body": "If a hazard develops, stop and make safe through the planned arrangements, alert the responsible people and report it. Do not wait for a valid cycle, a tutorial result or a successful network response. Use the approved communication route if the app is unavailable. On a facility evacuation alarm, stop all work, make worksites safe and evacuate as soon as possible; follow the JHSEP emergency action where abrupt stopping itself would be dangerous.",
          "bullets": [
            "The factual hazard report is separate from authorised PH/RS confirmations.",
            "Follow the emergency plan where abrupt stopping would itself be dangerous.",
            "An alarm all-clear is not by itself permission for everyone to restart."
          ]
        },
        {
          "heading": "Restart through the correct route",
          "body": "For a suspended ordinary Class B cycle, resolve the condition and repeat the required pre-issue, validation and PH acceptance process. Required testing, worksite checks and briefing evidence must be current. The old acceptance remains history; it cannot authorise the renewed cycle.",
          "bullets": [
            "Follow the applicable alarm branch: false-alarm restart may use RS verbal reauthorisation to each PH.",
            "Where the emergency-suspension branch is required, retain its distinct RS actions, normal revalidation and further worksite examination.",
            "Do not apply ordinary A/B validation automatically to Task SOP/NoW or other distinct routes."
          ]
        },
        {
          "heading": "A renewed cycle under the same authorisation",
          "body": "After the stop cause is resolved, the new cycle needs current controls, required evidence, briefing, acknowledgements and fresh PH acceptance. An existing OM approval remains tied to the same authorisation; a new authorisation needs a new OM approval."
        }
      ],
      "remember": [
        "Avoid: waiting for an app connection, current licence or valid gas test before raising a hazard.",
        "Avoid: treating all-clear or a later acceptable reading as automatic restart authority.",
        "Avoid: reusing an earlier acceptance or cached eligibility after a stop, changed condition or expiry."
      ],
      "checks": [
        {
          "id": "cur-09-check-1",
          "prompt": "The cycle has expired and a dangerous change is seen. What first?",
          "options": [
            {
              "id": "other-1",
              "text": "Wait for permit renewal first.",
              "feedback": "Required physical safety action and reporting do not wait for renewed work authority."
            },
            {
              "id": "correct",
              "text": "Stop safely; alert responsible people.",
              "feedback": "Use the planned safe-state and communication arrangements immediately; formal confirmations follow with their actual actors, even if the app is unavailable."
            },
            {
              "id": "other-2",
              "text": "Wait until the connection returns.",
              "feedback": "Use the approved communication route; lack of a connection must not delay stopping or alarm actions."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-09-check-2",
          "prompt": "A facility alarm ends with an all-clear. How does work restart?",
          "options": [
            {
              "id": "other-1",
              "text": "Restart automatically when the alarm ends.",
              "feedback": "All-clear alone does not provide the required restart authority."
            },
            {
              "id": "other-2",
              "text": "Reuse old acceptance without further checks.",
              "feedback": "Follow the applicable branch, including renewed acceptance where the required revalidation route applies."
            },
            {
              "id": "correct",
              "text": "Follow the required RS restart route.",
              "feedback": "False-alarm restart may use RS verbal reauthorisation to each PH; where real-alarm emergency suspension is required, preserve RS's on-behalf-of-PH and separate RS actions, normal revalidation and further worksite examination."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-040",
        "CORE-061",
        "CORE-062",
        "CORE-063",
        "CORE-064",
        "CORE-068",
        "CORE-070",
        "SYS-016"
      ]
    },
    {
      "id": "cur-10",
      "title": "Cancel, close out and preserve records",
      "summary": "Distinguish stopping the work, site closeout and records completion.",
      "roles": [
        "PA",
        "RS",
        "PH"
      ],
      "objectives": [
        "Record complete or incomplete work honestly and leave site/equipment safe.",
        "Separate PH cancellation from RS inspection/closeout and accountable isolation disposition.",
        "Preserve signed versions, original-paper identity and archive status without reviving work."
      ],
      "sections": [
        {
          "heading": "PH cancellation stops further work",
          "body": "Cancellation can record complete or incomplete work. PH secures the site/equipment and signs the true status. Further work stops immediately when cancellation is signed; it does not continue while waiting for RS closeout.",
          "bullets": [
            "Do not claim completion simply to close a record.",
            "Record outstanding work, limitations and the required new permit or transfer.",
            "Retained isolations need accountable disposition under the approved isolation process."
          ]
        },
        {
          "heading": "RS closeout and archive are separate",
          "body": "The RS performs the required worksite/status checks and records closeout. Required isolation disposition, operation checks and evidence remain accountable. Archive completion is a further records task; failed delivery does not reopen the permit.",
          "bullets": [
            "Preserve signed content and record corrections explicitly.",
            "Retain paper-original identity and signatory evidence separately from later electronic entry.",
            "Use the approved record-type retention schedule; this tutorial creates no deletion period."
          ]
        }
      ],
      "remember": [
        "Avoid: marking work complete to obtain a clean dashboard when it is incomplete.",
        "Avoid: continuing work while waiting for RS closeout after PH cancellation.",
        "Avoid: treating closeout/archive as permission to remove isolation or silently rewriting old evidence."
      ],
      "checks": [
        {
          "id": "cur-10-check-1",
          "prompt": "PH has signed Work Incomplete cancellation. What happens to further work?",
          "options": [
            {
              "id": "correct",
              "text": "Stop; await the required follow-on route.",
              "feedback": "PH cancellation stops further work immediately; the true incomplete status, safe condition, RS closeout and isolation disposition remain separate requirements."
            },
            {
              "id": "other-1",
              "text": "Continue until RS records final closeout.",
              "feedback": "There is no work grace period between PH cancellation and RS closeout."
            },
            {
              "id": "other-2",
              "text": "Change status so the job can continue.",
              "feedback": "Do not alter the true outcome or restore work authority to avoid the required follow-on process."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-10-check-2",
          "prompt": "Archive delivery fails after cancellation. What should happen?",
          "options": [
            {
              "id": "other-2",
              "text": "Replace signatures with new records.",
              "feedback": "Preserve the signed evidence; an archive failure is not permission to recreate signatures."
            },
            {
              "id": "correct",
              "text": "Retry archive; keep work cancelled.",
              "feedback": "Resolve delivery as a visible records task while preserving signed evidence and cancelled work status."
            },
            {
              "id": "other-1",
              "text": "Reopen work while archive retries.",
              "feedback": "Archive delivery is distinct from work authority and cannot reopen a cancelled permit."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-070",
        "CORE-071",
        "CORE-072",
        "CORE-073",
        "ACT-015",
        "ACT-017",
        "ACT-019",
        "SYS-010",
        "SYS-012",
        "SYS-013"
      ]
    },
    {
      "id": "cur-11",
      "title": "Supporting controls, clients and outages",
      "summary": "Know when another procedure or authorised route is needed.",
      "roles": [
        "ALL"
      ],
      "objectives": [
        "Recognise when CSE, isolation, pressure methods or other specialist arrangements are required and stop at an unsupported route.",
        "Locate the approved technical procedure, competent authority and linked records instead of substituting an upload.",
        "Use the approved outage/continuity route and preserve original paper evidence for later reconciliation."
      ],
      "sections": [
        {
          "heading": "A permit does not replace the technical method",
          "body": "Isolation, CSE entry/rescue, pressure testing and Surface Preparation require their applicable approved controls and competent people. This tutorial explains interfaces only. It does not teach technical execution or provide acceptance limits.",
          "bullets": [
            "Where a native platform route is unavailable, an upload cannot enable it.",
            "Follow the actual approved client/site issuing boundary.",
            "A draft procedure, training example or software administrator role does not grant operating authority."
          ]
        },
        {
          "heading": "Use an approved continuity process",
          "body": "Cached data is historical evidence until current authorisation can be confirmed. During an outage, follow the approved communications/paper-continuity arrangement and preserve conflict visibility, signatories, original identifiers and times. Later entry is reconciliation, not a new electronic signature by the original paper signer.",
          "bullets": [
            "Physical safety action does not wait for the app.",
            "Keep original paper actions separate from who entered them and when.",
            "Complete the required reassessment and authority checks before affected work restarts."
          ]
        }
      ],
      "remember": [
        "Avoid: assuming a CSE certificate upload authorises entry or a generic isolation checkbox proves safety.",
        "Avoid: learning a rescue technique, gas threshold, pressure setting or isolation exception from a tutorial example.",
        "Avoid: treating a paper scan as a new electronic signature by the original signer or treating admin access as operational authority."
      ],
      "checks": [
        {
          "id": "cur-11-check-1",
          "prompt": "A CSE certificate is attached. What is still needed for entry?",
          "options": [
            {
              "id": "other-1",
              "text": "Upload it to enable any entry.",
              "feedback": "An upload cannot enable an unsupported route or verify the complete entry controls."
            },
            {
              "id": "other-2",
              "text": "Use ordinary PH acceptance for entry.",
              "feedback": "Ordinary permit acceptance does not replace the distinct approved CSE authorisation and controls."
            },
            {
              "id": "correct",
              "text": "Use the full approved entry route.",
              "feedback": "Keep unsupported routes unavailable; any approved paper/external route must provide the competent entry authority and complete linked PTW, CSE, isolation, gas, entry-log and rescue arrangements."
            }
          ],
          "correctOptionId": "correct"
        },
        {
          "id": "cur-11-check-2",
          "prompt": "A safety problem develops during an outage. What should happen?",
          "options": [
            {
              "id": "correct",
              "text": "Make safe; preserve original paper evidence.",
              "feedback": "Take the required physical safety action and use the approved fallback/communications route; record original paper signers and times separately from later entry and reconciliation."
            },
            {
              "id": "other-1",
              "text": "Keep working from the cached status.",
              "feedback": "Cached status does not prove current authority or maintained controls; follow the approved continuity and safety arrangements."
            },
            {
              "id": "other-2",
              "text": "Wait; recreate electronic signatures after recovery.",
              "feedback": "Do not delay physical safety action or impersonate original signers; later entry preserves the original evidence and actual recorder."
            }
          ],
          "correctOptionId": "correct"
        }
      ],
      "ruleIds": [
        "CORE-033",
        "CORE-042",
        "CORE-044",
        "CORE-053",
        "CORE-054",
        "CORE-065",
        "GOV-022",
        "GOV-027",
        "SYS-004",
        "SYS-012",
        "SYS-014",
        "SYS-016"
      ]
    }
  ],
  "practice": {
    "title": "Practise the Class B journey",
    "description": "A fictional Surface Preparation job: review the package, daily checks, stop, restart and cancellation decisions.",
    "notice": "Practice only. No permits, signatures, appointments or operational records are created.",
    "steps": [
      {
        "id": "practice-01",
        "title": "Define the work",
        "actor": "PA",
        "prompt": "Fictional exercise: hand-tool preparation of three pipe supports at Adam Test Site. Class B has been selected for this synthetic scenario only. What must the application identify?",
        "options": [
          {
            "id": "b",
            "text": "Only the service name Surface Preparation.",
            "feedback": "A service label is not a complete scope or assessment."
          },
          {
            "id": "c",
            "text": "Any nearby equipment, so extra tasks can be added later.",
            "feedback": "The work boundary must be explicit; a permit is not an open-ended work order."
          },
          {
            "id": "a",
            "text": "The exact supports/boundary, hand-tool method, hazards/controls and applicable procedure.",
            "feedback": "Correct. The fictional Class B selection is not a classification rule for real Surface Preparation."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-017",
          "CORE-027",
          "ACT-069"
        ]
      },
      {
        "id": "practice-02",
        "title": "Agree the interfaces",
        "actor": "OAC and RS",
        "prompt": "The neighbouring area has an activity that may affect the job. What is needed before the required agreement and authorisation?",
        "options": [
          {
            "id": "c",
            "text": "Tick agreement now and collect the hazards later.",
            "feedback": "An unexplained tick does not establish that the precautions are satisfied."
          },
          {
            "id": "a",
            "text": "Assess the interaction, record the precautions and obtain the required affected-area agreement against the correct package.",
            "feedback": "Correct. The agreement must include the actual interface controls."
          },
          {
            "id": "b",
            "text": "Ignore it because another team owns that area.",
            "feedback": "Affected-area ownership is the reason to coordinate the interface."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-045",
          "CORE-047",
          "CORE-049"
        ]
      },
      {
        "id": "practice-om",
        "title": "Independent Operations Manager approval",
        "actor": "OM",
        "prompt": "The RS authorised the permit. What is still needed before the first daily cycle?",
        "options": [
          {
            "id": "correct",
            "text": "The independent OM approval of this authorisation.",
            "feedback": "The OM approval is required before any daily cycle."
          },
          {
            "id": "other-1",
            "text": "Nothing; RS authorisation is enough.",
            "feedback": "Authorisation does not replace OM approval."
          },
          {
            "id": "other-2",
            "text": "A PH signature on behalf of the OM.",
            "feedback": "The PH cannot supply another person's approval."
          }
        ],
        "correctOptionId": "correct",
        "ruleIds": []
      },
      {
        "id": "practice-03",
        "title": "Review today’s cycle",
        "actor": "RS",
        "prompt": "The overall permit remains authorised. The team wants a new daily work cycle. What happens next?",
        "options": [
          {
            "id": "a",
            "text": "Perform the current pre-issue plan/conflict/SIMOPS/custodian review.",
            "feedback": "Correct. Overall authorisation does not replace each daily cycle’s controls."
          },
          {
            "id": "b",
            "text": "Reuse yesterday’s pre-issue automatically.",
            "feedback": "The review must account for current work and conditions."
          },
          {
            "id": "c",
            "text": "Skip to PH acceptance because the overall period remains valid.",
            "feedback": "Pre-issue, RS validation and PH acceptance are distinct required stages."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-052",
          "CORE-066"
        ]
      },
      {
        "id": "practice-04",
        "title": "Complete before-work testing",
        "actor": "RS and AGT",
        "prompt": "RS records a required gas test as pre-arranged before work. The test has not happened. May work start?",
        "options": [
          {
            "id": "b",
            "text": "Yes, validation means all gas conditions are satisfied.",
            "feedback": "The pre-arrangement still needs actual completion."
          },
          {
            "id": "c",
            "text": "Yes, if someone enters a familiar safe limit.",
            "feedback": "A familiar value is not the applicable approved criterion or an actual test."
          },
          {
            "id": "a",
            "text": "No. Complete the required actual test and other before-work conditions, then follow the required acceptance route.",
            "feedback": "Correct. Pre-arranged at validation is not a completed acceptable test."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-038",
          "CORE-044",
          "CORE-055",
          "CORE-058"
        ]
      },
      {
        "id": "practice-05",
        "title": "Brief and accept individually",
        "actor": "PH and crew",
        "prompt": "One crew member has not acknowledged the briefing. A colleague offers to sign using that person’s account. What should happen?",
        "options": [
          {
            "id": "c",
            "text": "Treat the PH signature as every crew member’s acknowledgement.",
            "feedback": "PH acceptance and individual crew understanding are separate records."
          },
          {
            "id": "a",
            "text": "Ensure the person understands and makes their own required acknowledgement; PH acceptance remains separate.",
            "feedback": "Correct. Another person cannot supply an individual’s electronic sign-off."
          },
          {
            "id": "b",
            "text": "Allow it because both people attended.",
            "feedback": "Attendance does not permit impersonation."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-058",
          "CORE-060",
          "SYS-005"
        ]
      },
      {
        "id": "practice-06",
        "title": "Stop for a changed condition",
        "actor": "Anyone",
        "prompt": "After the work starts, a crew member spots loose grating. The app connection is unavailable. What is the first response?",
        "options": [
          {
            "id": "a",
            "text": "Stop/make safe through the planned arrangements, alert the responsible people and use the approved reporting route.",
            "feedback": "Correct. Physical safety does not wait for a connection or an expired/valid cycle."
          },
          {
            "id": "b",
            "text": "Keep working until the app confirms a report.",
            "feedback": "Do not wait for a network response before required safety action."
          },
          {
            "id": "c",
            "text": "Wait for the next lesson to unlock stop reporting.",
            "feedback": "Learning progress must never restrict stopping or hazard reporting."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-061",
          "CORE-062",
          "CORE-064"
        ]
      },
      {
        "id": "practice-07",
        "title": "Revalidate and accept again",
        "actor": "RS, RS and PH",
        "prompt": "The hazard is resolved after a recorded suspension. What is needed to restart this ordinary Class B exercise?",
        "options": [
          {
            "id": "b",
            "text": "Only an acceptable gas reading.",
            "feedback": "Another test does not resolve every control or restore work authority."
          },
          {
            "id": "c",
            "text": "Clear the stop flag and reuse the old acceptance.",
            "feedback": "Follow the authorised revalidation and renewed acceptance process."
          },
          {
            "id": "a",
            "text": "The required renewed pre-issue/validation checks and fresh PH acceptance, with current testing/briefing evidence as required.",
            "feedback": "Correct. The previous acceptance is preserved as history, not reused as authority for the renewed cycle."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-063",
          "CORE-068",
          "SYS-016"
        ]
      },
      {
        "id": "practice-08",
        "title": "Cancel truthfully",
        "actor": "PH",
        "prompt": "The permit must end while some work remains incomplete. What should PH record?",
        "options": [
          {
            "id": "c",
            "text": "Work Incomplete, then continue until RS closes out.",
            "feedback": "PH cancellation stops further work immediately."
          },
          {
            "id": "a",
            "text": "The true incomplete status and safe condition, then cancellation. Further work stops when cancellation is signed.",
            "feedback": "Correct. Do not force a false completion; the required follow-on/transfer and RS closeout remain separate."
          },
          {
            "id": "b",
            "text": "Work Complete, so the record can close.",
            "feedback": "The recorded status must be true."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-070",
          "CORE-071",
          "CORE-072"
        ]
      },
      {
        "id": "practice-09",
        "title": "Close out and preserve evidence",
        "actor": "RS and records support",
        "prompt": "RS completes the required closeout. Archive delivery then fails. What happens to work authority?",
        "options": [
          {
            "id": "a",
            "text": "It remains cancelled/closed out; resolve the outstanding archive task without reopening work.",
            "feedback": "Correct. Records delivery is distinct from permission to work."
          },
          {
            "id": "b",
            "text": "Reopen the permit until the archive succeeds.",
            "feedback": "An archive failure is not a reason to restore work authority."
          },
          {
            "id": "c",
            "text": "Delete the signatures and create a replacement automatically.",
            "feedback": "Preserve signed evidence and correct the records task without inventing new authority."
          }
        ],
        "correctOptionId": "a",
        "ruleIds": [
          "CORE-071",
          "CORE-072",
          "SYS-013"
        ]
      }
    ]
  },
  "glossary": [
    {
      "term": "PTW",
      "meaning": "Permit to Work: a controlled process for coordinating defined work, hazards, people and prerequisites."
    },
    {
      "term": "PA",
      "meaning": "Permit Applicant: prepares and submits the work package under the applicable approved authority."
    },
    {
      "term": "RS",
      "meaning": "Responsible Supervisor: reviews, authorises, pre-issues, validates daily cycles, confirms stops and closes out. Pre-issue and validation are separate signatures. Formal gas testing needs an AGT."
    },
    {
      "term": "PH",
      "meaning": "Permit Holder: briefs, accepts and supervises the defined work, and records required stopping/cancellation actions."
    },
    {
      "term": "AGT",
      "meaning": "Authorised Gas Tester: a person with the applicable formal testing qualification, authority and scope."
    },
    {
      "term": "OAC",
      "meaning": "Other Affected Custodian: the authorised affected-area representative; the reference scheme requires the relevant licensed RS authority."
    },
    {
      "term": "PC",
      "meaning": "Production Coordinator: additional approval/coordination function where the governing route requires it."
    },
    {
      "term": "IA",
      "meaning": "Isolating Authority: the applicable competent isolation function; a software role alone does not establish technical authority."
    },
    {
      "term": "JHSEP",
      "meaning": "Job Health, Safety and Environment Plan: the job-specific hazards, controls and supporting arrangements under the approved process."
    },
    {
      "term": "TRIC",
      "meaning": "Toolbox Talk Risk Identification Card: the applicable work-party briefing/risk communication record; it cannot enlarge permit scope."
    },
    {
      "term": "TBT / TBM",
      "meaning": "Toolbox talk / toolbox meeting: the actual job briefing and discussion, with required individual understanding/attendance evidence."
    },
    {
      "term": "DRA",
      "meaning": "Dynamic risk assessment: continued review of changing hazards and controls; it is not permission for work outside the boundary."
    },
    {
      "term": "SIMOPS",
      "meaning": "Simultaneous operations: interactions between nearby/concurrent work and operations that need coordination."
    },
    {
      "term": "MOPO",
      "meaning": "Manual of Permitted Operations: the adopted operating-constraints matrix and its stop/caution guidance."
    },
    {
      "term": "CSE",
      "meaning": "Confined-space entry: a distinct controlled entry/rescue arrangement linked to the PTW, not replaced by a document upload."
    },
    {
      "term": "ICC",
      "meaning": "Isolation Confirmation Certificate: linked isolation evidence governed by its separate technical/authority process."
    },
    {
      "term": "DFT",
      "meaning": "De-isolation for test: a separately controlled test/restoration cycle; not general permission to remove isolation."
    },
    {
      "term": "Task SOP / NoW",
      "meaning": "A distinct Task SOP / Notification of Work route with its own approved process; do not apply ordinary A/B validation automatically."
    },
    {
      "term": "NPJ",
      "meaning": "No Permit Job: a separate approved control route, not a general exemption from safe methods or responsibility."
    },
    {
      "term": "Authorisation",
      "meaning": "Approval of the defined package/overall interval under the governing route; distinct from daily validation and PH acceptance."
    },
    {
      "term": "Revalidation",
      "meaning": "Repeating the required current validation and acceptance process before renewed work."
    },
    {
      "term": "Cancellation and closeout",
      "meaning": "PH cancellation stops further work; RS closeout and records/archive tasks remain distinct."
    },
    {
      "term": "AA (historic)",
      "meaning": "Area Authority: not a current BadrEOR role. Those duties belong to the RS; older permit records and older sandboxes may retain AA as originally signed."
    },
    {
      "term": "OM",
      "meaning": "Operations Manager: independently approves each RS-authorised permit before a daily cycle. Never the RS, PA or PH of that permit. A new authorisation needs a new approval."
    }
  ],
  "journey": {
    "title": "Follow one permit from start to finish",
    "job": {
      "title": "Prepare three pipe supports",
      "location": "Adam Test Site — fictional training location",
      "scope": "Hand-tool preparation of three identified pipe supports; no additional equipment or tasks.",
      "method": "Hand-tool Surface Preparation, using the method and controls supplied for this fictional exercise.",
      "notice": "Practice only. Class B is selected for this synthetic example; it does not classify real Surface Preparation work. These teaching screens are conceptual examples, not verified screenshots of the current app. No operational permits or signatures are created."
    },
    "chapters": [
      {
        "id": "before-job",
        "title": "Before the job"
      },
      {
        "id": "before-work",
        "title": "Before work starts"
      },
      {
        "id": "during-work",
        "title": "During work"
      },
      {
        "id": "finishing",
        "title": "Finishing"
      }
    ],
    "steps": [
      {
        "id": "prepare",
        "chapterId": "before-job",
        "title": "Define the work",
        "actor": "Permit Applicant (PA)",
        "roles": [
          "PA"
        ],
        "instruction": "Find the field that tells everyone exactly where the work ends.",
        "why": "A clear boundary helps reviewers assess the right hazards and prevents extra tasks being added without approval.",
        "stopIf": "The task, equipment boundary or required information is unclear.",
        "nextActor": "RS and the permit review team",
        "details": [
          "PA prepares the application, JHSEP and relevant supporting evidence for the same work and revision.",
          "Use the approved planning and urgency route. Urgency does not remove safety controls.",
          "Activity groups and the Hydrostatic, Pneumatic and Hydraulic test types describe work; actual method, medium, location and hazards determine the applicable route.",
          "Obtain missing approved area data, technical criteria and risk information instead of guessing."
        ],
        "terms": [
          "PA",
          "JHSEP",
          "PTW"
        ],
        "ruleIds": [
          "CORE-017",
          "CORE-022",
          "CORE-026",
          "CORE-027",
          "CORE-028",
          "CORE-031",
          "CORE-032",
          "CORE-033",
          "ACT-069",
          "ACT-070"
        ],
        "screen": {
          "title": "Application — identify the work boundary",
          "tab": "Work description",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Application draft",
          "annotation": "Find the exact work boundary.",
          "regions": [
            {
              "id": "service",
              "label": "Service",
              "value": "Surface Preparation",
              "kind": "field",
              "feedback": "This names the service. The actual method and work boundary still need to be stated."
            },
            {
              "id": "boundary",
              "label": "Work boundary",
              "value": "Supports A, B and C only",
              "kind": "field",
              "feedback": "This identifies the three supports. Additional equipment or tasks need the appropriate scope review and permit route."
            },
            {
              "id": "method",
              "label": "Method",
              "value": "Hand-tool preparation",
              "kind": "field",
              "feedback": "The method informs the assessment. It does not identify which supports are included."
            },
            {
              "id": "period",
              "label": "Requested period",
              "value": "As planned for this exercise",
              "kind": "field",
              "feedback": "The requested interval is part of the application. It does not extend the physical work boundary."
            }
          ],
          "correctTargetId": "boundary"
        }
      },
      {
        "id": "review",
        "chapterId": "before-job",
        "title": "Check affected areas",
        "actor": "RS and Other Affected Custodian (OAC)",
        "roles": [
          "PA",
          "RS",
          "OAC"
        ],
        "instruction": "Find the agreement needed because work in the neighbouring area could affect this job.",
        "why": "The people responsible for an affected area must help identify its hazards and precautions before the required agreement and authorisation.",
        "stopIf": "An affected-area control or required approval remains unresolved.",
        "nextActor": "RS for authorisation, then OM for independent approval",
        "details": [
          "OAC must have the relevant affected-area licensed RS authority under the reference scheme; a nearby supervisor or account label is insufficient.",
          "Record additional hazards and precautions in the JHSEP. OAC countersigns the relevant package only when satisfied.",
          "RS retains authorisation accountability. No one may authorise a permit for work they will perform themselves.",
          "PC involvement depends on the governing route. The unresolved Class B isolation-related trigger requires an authorised interpretation; do not silently bypass or universally apply it.",
          "Significant changes require the appropriate revision and resubmission. Preserve review and signatures against the package actually assessed."
        ],
        "terms": [
          "OAC",
          "RS",
          "PC",
          "JHSEP",
          "SIMOPS"
        ],
        "ruleIds": [
          "CORE-025",
          "CORE-045",
          "CORE-046",
          "CORE-047",
          "CORE-048",
          "CORE-049",
          "CORE-075"
        ],
        "screen": {
          "title": "Review — neighbouring-area interface",
          "tab": "Affected areas",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Review incomplete",
          "annotation": "Find the missing affected-area agreement.",
          "regions": [
            {
              "id": "application",
              "label": "Application",
              "value": "Ready for review",
              "kind": "status",
              "feedback": "The application is ready to assess. That does not supply the affected area's agreement."
            },
            {
              "id": "interface",
              "label": "Nearby work",
              "value": "Interaction identified",
              "kind": "field",
              "feedback": "The interaction must be assessed and its precautions included in the package."
            },
            {
              "id": "agreement",
              "label": "OAC agreement",
              "value": "Pending affected-area review",
              "kind": "person",
              "feedback": "The affected-area custodian reviews the hazards and precautions and gives their own required agreement before authorisation can proceed."
            },
            {
              "id": "authorise",
              "label": "RS authorisation",
              "value": "Waiting for required review",
              "kind": "action",
              "feedback": "RS authorisation follows the completed required review; it cannot supply the missing OAC agreement."
            }
          ],
          "correctTargetId": "agreement"
        }
      },
      {
        "id": "om-approve",
        "chapterId": "before-job",
        "title": "Approve before the first cycle",
        "actor": "Operations Manager (OM)",
        "roles": [
          "OM",
          "RS",
          "PA"
        ],
        "instruction": "Find the missing approval that prevents the first daily cycle being requested.",
        "why": "The OM provides the independent second check once per authorisation. Until that approval exists, no daily cycle can be requested.",
        "stopIf": "The authorised package is unclear or unsafe, or the OM is also this permit's RS, PA or PH.",
        "nextActor": "PA, PH or RS to request the first daily cycle",
        "details": [
          "The OM checks the RS-authorised scope, location, hazards, controls, SIMOPS and named people.",
          "A new authorisation needs a new OM approval. Restarting under the same authorisation does not.",
          "The OM is a separate person from the RS, PA and PH of this permit."
        ],
        "terms": [
          "OM",
          "RS",
          "Authorisation"
        ],
        "ruleIds": [],
        "screen": {
          "title": "Authorised permit: independent approval",
          "tab": "Approvals",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Authorised; OM approval pending",
          "annotation": "Find what must be recorded before the cycle can be requested.",
          "regions": [
            {
              "id": "rs-authorisation",
              "label": "RS authorisation",
              "value": "Authorised",
              "kind": "status",
              "feedback": "RS authorisation is complete, but the independent OM approval is still required."
            },
            {
              "id": "om-approval",
              "label": "OM approval",
              "value": "Pending",
              "kind": "person",
              "feedback": "Correct. The independent OM approval is required before the first cycle."
            },
            {
              "id": "request-cycle",
              "label": "Request cycle",
              "value": "Unavailable: OM approval required",
              "kind": "action",
              "feedback": "This action remains blocked until the OM approves the current authorisation."
            },
            {
              "id": "ph-acceptance",
              "label": "PH acceptance",
              "value": "Not yet due",
              "kind": "status",
              "feedback": "PH acceptance follows the cycle controls; it cannot supply the OM approval."
            }
          ],
          "correctTargetId": "om-approval"
        }
      },
      {
        "id": "daily",
        "chapterId": "before-work",
        "title": "Review today's plan",
        "actor": "Responsible Supervisor (RS)",
        "roles": [
          "PA",
          "RS"
        ],
        "instruction": "Find the RS review needed for today's work cycle.",
        "why": "Work nearby and operating conditions can change while the permit remains authorised. Each daily cycle needs its current pre-issue review.",
        "stopIf": "Today's plan, conflicts or required interface controls have not been checked.",
        "nextActor": "RS for validation: the same RS or a second RS",
        "details": [
          "RS pre-issues before every daily validation, checking the current plan or approved change, conflicts, understood SIMOPS controls and affected-custodian notification.",
          "Overall authorisation, daily pre-issue, RS validation and PH acceptance are distinct stages.",
          "Current role, area/work scope and person-level separation requirements still apply.",
          "PA, PH or RS may request the cycle after OM approval. Authorisation, OM approval, pre-issue, validation and PH acceptance are distinct stages."
        ],
        "terms": [
          "RS",
          "SIMOPS",
          "Authorisation"
        ],
        "ruleIds": [
          "CORE-049",
          "CORE-052",
          "CORE-066",
          "CORE-074",
          "CORE-075"
        ],
        "screen": {
          "title": "Daily cycle — today's RS review",
          "tab": "Pre-issue",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Today's pre-issue pending",
          "annotation": "Find the review that must be current today.",
          "regions": [
            {
              "id": "authorised",
              "label": "Overall permit",
              "value": "Within authorised period",
              "kind": "status",
              "feedback": "The overall period does not replace today's plan and conflict review."
            },
            {
              "id": "previous",
              "label": "Earlier pre-issue",
              "value": "Previous cycle only",
              "kind": "field",
              "feedback": "An earlier review remains part of the history. Today's cycle needs its own required checks."
            },
            {
              "id": "preissue",
              "label": "Today's RS pre-issue",
              "value": "Plan and conflicts unchecked",
              "kind": "action",
              "feedback": "RS checks today's plan, conflicts and interface controls, and records the current pre-issue before RS validation."
            },
            {
              "id": "acceptance",
              "label": "PH acceptance",
              "value": "Not yet due",
              "kind": "person",
              "feedback": "PH acceptance follows the applicable current validation and safe-site checks. It cannot replace RS pre-issue."
            }
          ],
          "correctTargetId": "preissue"
        }
      },
      {
        "id": "gas",
        "chapterId": "before-work",
        "title": "Complete required testing",
        "actor": "RS and Authorised Gas Tester (AGT)",
        "roles": [
          "RS",
          "AGT"
        ],
        "instruction": "Find the missing evidence that prevents work starting, even though the test has been arranged.",
        "why": "An arrangement is a plan. A required before-work test must actually be completed against the applicable approved method and criteria.",
        "stopIf": "A required test, approved criterion or other before-work control is missing or unsafe.",
        "nextActor": "PH for briefing, safe-site checks and own acceptance",
        "details": [
          "The reference permits a required gas test to be prearranged at RS validation for completion before work. That allowance does not permit work to begin without it.",
          "The planned synthetic example requires the completed acceptable required test before PH acceptance; that sequencing is an explicit design choice, not an additional universal PDO clause.",
          "Use the applicable full RS validation checklist, including the required prerequisite controls, isolation confirmation, methods and worksite examination. A gas test alone does not satisfy it.",
          "Formal tests require current AGT qualification and scope. Record actual tester, observation time, readings, units, method/instrument and approved criteria reference.",
          "Monitor training alone does not authorise formal gas testing. Keep signed formal tests distinct from continuous monitoring.",
          "This exercise supplies no operating gas limits or sampling/calibration instructions. Preserve unsafe results and use the required stop, suspension and notification route.",
          "The RS needs no gas-tester certificate. Formal tests are signed by the AGT; validation remains the RS duty."
        ],
        "terms": [
          "RS",
          "AGT",
          "PH"
        ],
        "ruleIds": [
          "CORE-036",
          "CORE-038",
          "CORE-040",
          "CORE-041",
          "CORE-044",
          "CORE-053",
          "CORE-055",
          "CORE-056",
          "CORE-058",
          "SYS-014"
        ],
        "screen": {
          "title": "Before-work evidence — required gas test",
          "tab": "Gas testing",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Required test pending",
          "annotation": "Find what is still missing before work can start.",
          "regions": [
            {
              "id": "arrangement",
              "label": "Test arrangement",
              "value": "AGT visit arranged",
              "kind": "field",
              "feedback": "The visit is arranged. A plan does not supply an actual test result."
            },
            {
              "id": "validation",
              "label": "RS validation",
              "value": "Test prearranged before work",
              "kind": "status",
              "feedback": "Prearrangement can be recorded at validation under the reference route. The required actual test remains a before-work condition."
            },
            {
              "id": "result",
              "label": "Required gas test",
              "value": "No completed result",
              "kind": "field",
              "feedback": "The actual required test is missing. Complete it against approved criteria and satisfy the other required controls and PH acceptance before work starts."
            },
            {
              "id": "holder",
              "label": "PH acceptance",
              "value": "Pending safe-site checks",
              "kind": "person",
              "feedback": "PH must check the actual safe site and make their own acceptance. That cannot waive the missing required test."
            }
          ],
          "correctTargetId": "result"
        }
      },
      {
        "id": "brief",
        "chapterId": "before-work",
        "title": "Brief and accept",
        "actor": "PH and the work party",
        "roles": [
          "PH",
          "CREW"
        ],
        "instruction": "Find the missing acknowledgement after the toolbox talk.",
        "why": "Each person must understand the work and controls. Crew acknowledgements and the PH's own safe-site acceptance are separate records.",
        "stopIf": "Someone does not understand the work, or a required control or acknowledgement is missing.",
        "nextActor": "PH and crew, once all start requirements are satisfied",
        "details": [
          "PH or PA gives the required worksite toolbox talk immediately before the job. PH is accountable for the complete TRIC and DRA plan.",
          "Cover the actual work, hazards, controls, responsibilities, conflicts, access/evacuation and questions. Each crew member gives their own required understanding acknowledgement.",
          "An attendance recorder may record facts but cannot supply another person's electronic signature. The individual PH account and acceptance remain separate.",
          "Only when satisfied the site is safe and the applicable current conditions are met does PH accept. Maintain effective supervision and required visual/audible contact during work.",
          "Use the approved changeover and renewed briefing route when the PH changes. Work stops if required PH supervision is unavailable."
        ],
        "terms": [
          "PH",
          "PA",
          "TBT / TBM",
          "TRIC",
          "DRA"
        ],
        "ruleIds": [
          "CORE-051",
          "CORE-057",
          "CORE-058",
          "CORE-059",
          "CORE-060",
          "CORE-061",
          "CORE-067",
          "GOV-014",
          "SYS-004",
          "SYS-005"
        ],
        "screen": {
          "title": "Toolbox talk — individual understanding",
          "tab": "Briefing and acceptance",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Crew acknowledgement missing",
          "annotation": "Find the briefing evidence still needed.",
          "regions": [
            {
              "id": "talk",
              "label": "Toolbox talk",
              "value": "Work and controls discussed",
              "kind": "field",
              "feedback": "Discussion matters. Each person's required understanding acknowledgement must also be recorded."
            },
            {
              "id": "crew",
              "label": "Crew understanding",
              "value": "One acknowledgement missing",
              "kind": "person",
              "feedback": "Check that the person understands, answer their questions and obtain their own required acknowledgement. A colleague cannot sign as them."
            },
            {
              "id": "validated",
              "label": "RS validation",
              "value": "Current exercise cycle",
              "kind": "status",
              "feedback": "Current validation does not replace the toolbox talk, individual understanding or PH acceptance."
            },
            {
              "id": "accept",
              "label": "PH acceptance",
              "value": "Own action still required",
              "kind": "action",
              "feedback": "PH makes their own acceptance when satisfied the site is safe and required conditions are met. That does not supply a missing crew acknowledgement."
            }
          ],
          "correctTargetId": "crew"
        }
      },
      {
        "id": "stop",
        "chapterId": "during-work",
        "title": "Stop for a hazard",
        "actor": "Anyone who sees the danger",
        "roles": [
          "PH",
          "RS",
          "AGT",
          "CREW",
          "OAC"
        ],
        "instruction": "Loose grating is found and the connection is down. Choose the immediate safety action.",
        "why": "Physical safety comes first. An expired cycle, missing connection or incomplete lesson never prevents stopping and reporting danger.",
        "stopIf": "A hazard develops, conditions change or a required control or supervision is lost.",
        "nextActor": "PH and RS, with RS and emergency support as required",
        "details": [
          "Stop and make safe through the planned arrangements, alert the responsible people and use the approved communication/reporting route.",
          "Record the actual hazard, reporter and time. A factual report is separate from authorised PH/RS confirmations; nobody impersonates another signatory.",
          "On a facility evacuation alarm, stop all work, make worksites safe and evacuate as soon as possible. Follow JHSEP emergency action where abrupt stopping itself would be dangerous.",
          "Preserve unsafe gas results and report them through the required stop/suspension route. Another acceptable reading alone does not restore authority.",
          "A TRIC discussion cannot enlarge the permit boundary. Different work needs the appropriate new permit/review route."
        ],
        "terms": [
          "PH",
          "RS",
          "DRA",
          "TRIC"
        ],
        "ruleIds": [
          "CORE-040",
          "CORE-061",
          "CORE-062",
          "CORE-063",
          "CORE-064",
          "CORE-070",
          "SYS-016"
        ],
        "screen": {
          "title": "Changed condition — connection unavailable",
          "tab": "Stop and report",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Hazard identified",
          "annotation": "Choose the physical response; no app connection is needed to act safely.",
          "regions": [
            {
              "id": "hazard",
              "label": "Observed hazard",
              "value": "Loose grating near the work",
              "kind": "field",
              "feedback": "This is the danger to report. Take the required physical safety action without waiting for electronic recording."
            },
            {
              "id": "connection",
              "label": "Connection",
              "value": "Unavailable",
              "kind": "status",
              "feedback": "Use the approved communication route. Connection loss must not delay required stopping or alarm actions."
            },
            {
              "id": "stop",
              "label": "Immediate action",
              "value": "Stop, make safe and alert",
              "kind": "action",
              "feedback": "Take the planned physical safety action now, alert the responsible people and use the approved reporting route. Formal confirmations follow with their actual actors."
            },
            {
              "id": "wait",
              "label": "Wait for connection",
              "value": "Delay electronic entry",
              "kind": "action",
              "feedback": "Electronic recording may need reconciliation later. Required stopping and communication must happen now."
            }
          ],
          "correctTargetId": "stop"
        }
      },
      {
        "id": "restart",
        "chapterId": "during-work",
        "title": "Check before restarting",
        "actor": "RS, RS and PH",
        "roles": [
          "PH",
          "RS"
        ],
        "instruction": "The hazard is resolved and RS has revalidated. Find the acceptance still needed for the renewed cycle.",
        "why": "The old acceptance belongs to the suspended cycle. PH must check the safe site and accept the renewed cycle themselves.",
        "stopIf": "The cause remains unresolved or a current restart control, test, check or acceptance is missing.",
        "nextActor": "PH and crew after the complete restart route",
        "details": [
          "For this suspended ordinary Class B exercise, resolve the cause and repeat the required current pre-issue, validation and PH acceptance process.",
          "Required tests, worksite checks, control evidence and briefing must meet the applicable current conditions. A single acceptable reading or cleared flag is insufficient.",
          "Keep valid old signatures as history. Check current authority for the attempted action and required continuing assignments without rewriting historical evidence.",
          "After a false alarm and all-clear, RS may verbally reauthorise each PH. Where the real-alarm emergency-suspension branch is required, RS acts on behalf of PH and separately as RS, followed by normal revalidation and further worksite examination.",
          "Do not apply this ordinary A/B sequence automatically to Task SOP/NoW, NPJ or other distinct approved routes. An all-clear alone is not restart authority.",
          "A restart under the same authorisation does not need a new OM approval; re-authorisation does."
        ],
        "terms": [
          "RS",
          "PH",
          "Revalidation",
          "Task SOP / NoW",
          "NPJ"
        ],
        "ruleIds": [
          "CORE-052",
          "CORE-063",
          "CORE-064",
          "CORE-068",
          "SYS-016"
        ],
        "screen": {
          "title": "Renewed cycle — final acceptance check",
          "tab": "Revalidation",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Fresh PH acceptance pending",
          "annotation": "Find what is missing from this renewed cycle.",
          "regions": [
            {
              "id": "cause",
              "label": "Hazard and required controls",
              "value": "Rechecked for this exercise",
              "kind": "field",
              "feedback": "Cause resolution and current controls are prerequisites. They do not replace each actor's required sign-off."
            },
            {
              "id": "revalidated",
              "label": "RS revalidation",
              "value": "Renewed cycle recorded",
              "kind": "person",
              "feedback": "RS has revalidated this fictional renewed cycle. PH still needs their own safe-site check and acceptance."
            },
            {
              "id": "old",
              "label": "Earlier PH acceptance",
              "value": "Suspended cycle history",
              "kind": "field",
              "feedback": "Preserve the earlier signature as history. It cannot authorise work in the renewed cycle."
            },
            {
              "id": "fresh",
              "label": "New PH acceptance",
              "value": "Pending for renewed cycle",
              "kind": "action",
              "feedback": "PH must satisfy themselves the site is safe, confirm the applicable current conditions and sign acceptance again before renewed work."
            }
          ],
          "correctTargetId": "fresh"
        }
      },
      {
        "id": "cancel",
        "chapterId": "finishing",
        "title": "Record the true outcome",
        "actor": "Permit Holder (PH)",
        "roles": [
          "PH",
          "PA"
        ],
        "instruction": "The permit must end with work remaining. Find the truthful cancellation outcome.",
        "why": "Work Incomplete is legitimate. PH secures the site and cancels truthfully; further work stops immediately when cancellation is signed.",
        "stopIf": "Cancellation has been signed or the current work authority has otherwise ended.",
        "nextActor": "RS for the required closeout and isolation disposition checks",
        "details": [
          "PH records complete or incomplete work and the safe condition, outstanding work and restrictions. Do not claim completion simply to close the record.",
          "After PH cancellation no further work occurs on that permit, including while waiting for RS closeout.",
          "Remaining work follows the appropriate new permit or other approved route. Required isolation transfer or Extended Period Isolation (EPI) arrangements are separate competent decisions.",
          "PA may support follow-on planning; PA does not supply the PH's cancellation signature."
        ],
        "terms": [
          "PH",
          "RS",
          "Cancellation and closeout"
        ],
        "ruleIds": [
          "CORE-070",
          "CORE-071",
          "CORE-072",
          "ACT-015",
          "ACT-017",
          "ACT-019"
        ],
        "screen": {
          "title": "Cancellation — truthful work status",
          "tab": "PH cancellation",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Work remains at permit end",
          "annotation": "Select the true outcome before the PH's own cancellation action.",
          "regions": [
            {
              "id": "remaining",
              "label": "Outstanding work",
              "value": "One support unfinished",
              "kind": "field",
              "feedback": "Outstanding work must remain visible with the safe condition and restrictions. It prevents a truthful claim that all work is complete."
            },
            {
              "id": "incomplete",
              "label": "Work Incomplete",
              "value": "Record the remaining work",
              "kind": "choice",
              "feedback": "Record the true incomplete status and safe condition. When PH signs cancellation, further work stops immediately; RS closeout and follow-on arrangements remain separate."
            },
            {
              "id": "complete",
              "label": "Work Complete",
              "value": "All planned work finished",
              "kind": "choice",
              "feedback": "That is not true in this exercise. Do not use a false completion to clear a record."
            },
            {
              "id": "continue",
              "label": "Continue until closeout",
              "value": "Wait for RS before stopping",
              "kind": "choice",
              "feedback": "There is no work grace period after cancellation. PH cancellation stops further work before RS closeout."
            }
          ],
          "correctTargetId": "incomplete"
        }
      },
      {
        "id": "closeout",
        "chapterId": "finishing",
        "title": "Finish the records",
        "actor": "RS and records support",
        "roles": [
          "RS"
        ],
        "instruction": "Choose the records task that fixes delivery without reopening work.",
        "why": "Closeout and archive delivery are separate tasks. A records failure cannot restore work authority or justify replacing signed evidence.",
        "stopIf": "Someone tries to continue cancelled work or remove isolation without its separate approved process.",
        "nextActor": "Records support to resolve delivery; competent authorities for any follow-on work",
        "details": [
          "RS performs the required site/status checks, confirms the complete/incomplete condition and records the accountable disposition of associated isolations.",
          "Isolation may be de-isolated or transferred through the approved route. Closing a permit or uploading evidence does not itself authorise removal.",
          "Keep cancelled work closed while resolving archive errors. Preserve signed versions, original-paper identities and actual signer/time evidence.",
          "Later electronic entry records the actual recorder and reconciliation time separately from original paper actions.",
          "Use the adopted record-type retention and hold/disposal arrangements. This tutorial supplies no universal deletion period."
        ],
        "terms": [
          "RS",
          "ICC",
          "Cancellation and closeout"
        ],
        "ruleIds": [
          "CORE-071",
          "CORE-072",
          "CORE-073",
          "ACT-015",
          "ACT-017",
          "SYS-010",
          "SYS-012",
          "SYS-013"
        ],
        "screen": {
          "title": "Closeout — outstanding archive delivery",
          "tab": "Records",
          "permitNumber": "TRAINING-ONLY-001",
          "status": "Cancelled; RS closeout complete",
          "annotation": "Choose the safe next records action.",
          "regions": [
            {
              "id": "work",
              "label": "Work authority",
              "value": "Cancelled",
              "kind": "status",
              "feedback": "It stays cancelled. Repairing records delivery must not restore work authority."
            },
            {
              "id": "delivery",
              "label": "Archive delivery",
              "value": "Failed; evidence retained",
              "kind": "field",
              "feedback": "Keep this failure visible and resolve delivery while preserving the signed package and cancelled work status."
            },
            {
              "id": "retry",
              "label": "Retry archive",
              "value": "Keep work cancelled",
              "kind": "action",
              "feedback": "Resolve the delivery task without reopening work or replacing signatures. The archive error remains visible until delivery succeeds."
            },
            {
              "id": "reopen",
              "label": "Reopen work",
              "value": "Restore work to retry",
              "kind": "action",
              "feedback": "An archive failure does not justify reopening a cancelled permit. The records task is separate."
            }
          ],
          "correctTargetId": "retry"
        }
      }
    ]
  },
  "helpTopics": [
    {
      "id": "start",
      "title": "When can work start?",
      "answer": "After the applicable current authorisation, daily checks, actual before-work controls and PH safe-site acceptance are complete.",
      "details": "RS authorisation, RS pre-issue, RS validation and PH acceptance have different purposes; no single signature or green status proves a safe worksite.",
      "terms": [
        "RS",
        "PH",
        "Authorisation"
      ]
    },
    {
      "id": "daily",
      "title": "Why repeat the RS review today?",
      "answer": "Nearby work and conditions may change, so each daily validation needs the current plan and conflict review.",
      "details": "The overall authorisation period does not replace RS pre-issue for the daily cycle.",
      "terms": [
        "RS",
        "SIMOPS"
      ]
    },
    {
      "id": "arranged",
      "title": "The gas test is arranged. Can we start?",
      "answer": "No. A required before-work test must actually be completed and acceptable, alongside the other start requirements.",
      "details": "Use the applicable approved criteria; this tutorial supplies no operating gas limits or testing qualification.",
      "terms": [
        "AGT",
        "RS",
        "PH"
      ]
    },
    {
      "id": "offline",
      "title": "I see danger but the app is offline. What first?",
      "answer": "Stop and make safe through the planned arrangements, alert the responsible people and use the approved communication route.",
      "details": "On a facility evacuation alarm, stop all work, make safe and evacuate as soon as possible; use planned emergency action where abrupt stopping itself is dangerous.",
      "terms": [
        "PH",
        "RS",
        "JHSEP"
      ]
    },
    {
      "id": "scope",
      "title": "Can we add another task to TRIC?",
      "answer": "TRIC cannot enlarge the permit boundary; stop affected work and obtain the appropriate scope assessment and permit route.",
      "details": "Use TRIC for the actual briefing and additional hazards/controls, not as authority for different work.",
      "terms": [
        "TRIC",
        "DRA"
      ]
    },
    {
      "id": "supervision",
      "title": "What if the PH must leave?",
      "answer": "Work cannot continue without the required PH supervision; use the approved handover route or stop the work.",
      "details": "PH must remain at or in close visual and audible contact with the worksite, and a successor needs the applicable approval, briefing and own acceptance.",
      "terms": [
        "PH",
        "RS"
      ]
    },
    {
      "id": "restart",
      "title": "The hazard is fixed. Can we restart?",
      "answer": "Follow the applicable restart route; a suspended ordinary Class B cycle needs current checks, revalidation and fresh PH acceptance.",
      "details": "An all-clear alone is insufficient: false-alarm restart may use RS verbal reauthorisation to each PH; the required real-alarm emergency-suspension branch uses its separate actions and revalidation.",
      "terms": [
        "Revalidation",
        "RS",
        "PH"
      ]
    },
    {
      "id": "incomplete",
      "title": "Can we cancel unfinished work?",
      "answer": "Yes. Record Work Incomplete and the safe condition truthfully; further work stops when PH signs cancellation.",
      "details": "RS closeout, remaining isolation disposition and any follow-on work route are separate tasks.",
      "terms": [
        "Cancellation and closeout",
        "PH",
        "RS"
      ]
    },
    {
      "id": "route",
      "title": "Does the activity name select its permit class?",
      "answer": "No. Assess the actual method, location, hazards and applicable approved route.",
      "details": "Keep Hydrostatic, Pneumatic and Hydraulic labels distinct with actual medium and method; a CSE or isolation attachment cannot enable an unsupported route or replace its approved controls.",
      "terms": [
        "PTW",
        "CSE",
        "ICC"
      ]
    },
    {
      "id": "authority",
      "title": "Does finishing this tutorial qualify me to sign?",
      "answer": "No. Learning progress is separate from approved training, competence assessment, licensing and appointment.",
      "details": "Choosing a learning role changes the guidance shown; it does not change your account, credentials or operating authority.",
      "terms": [
        "PA",
        "RS",
        "PH",
        "AGT",
        "OAC"
      ]
    },
    {
      "id": "om-pending",
      "title": "The permit is authorised. Why can we not request a cycle?",
      "answer": "The independent Operations Manager approval is still required.",
      "details": "The OM must approve the current authorisation before any daily cycle. A new authorisation needs a new approval.",
      "terms": [
        "OM",
        "Authorisation"
      ]
    },
    {
      "id": "rs-validation",
      "title": "Can the same RS pre-issue and validate?",
      "answer": "Yes. They are two separate signatures; a second RS may validate instead.",
      "details": "The RS performs all sixteen validation checks and the required worksite examination. The RS duty does not itself require gas-tester qualification.",
      "terms": [
        "RS",
        "Validation"
      ]
    }
  ]
}
